import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Popover } from 'antd';
import Picker from 'emoji-picker-react';
import { CommentBoxWrapper } from './styled';

interface Props {
  initValue: string;
  loading?: boolean;
  isShowBtnReset?: boolean;
  isShowBtnCancel?: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

const CommentBox = memo(
  ({
    initValue,
    loading,
    isShowBtnCancel = true,
    isShowBtnReset = false,
    onSubmit,
    onCancel,
  }: Props) => {
    const inputRef = useRef<HTMLTextAreaElement | null>(null!);
    const [value, setValue] = useState('');

    const onCheckEscape = useCallback(
      event => {
        if (event.key === 'Escape') onCancel();
        if (event.keyCode === 13) {
          if (!event.shiftKey) {
            onCancel();
            onSubmit(value);
            event.preventDefault();
          }
        }
      },
      [onCancel, onSubmit, value],
    );

    useEffect(() => {
      const inputInstance = inputRef.current;
      if (inputInstance)
        inputInstance.addEventListener('keydown', onCheckEscape);
      return () => {
        if (inputInstance)
          inputInstance.removeEventListener('keydown', onCheckEscape);
      };
    }, [onCheckEscape]);

    useEffect(() => {
      inputRef.current?.focus();
      if (value === null) setValue(initValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initValue]);

    const onEmojiClick = useCallback((_e, emoji) => {
      setValue(prev => prev + emoji.emoji);
    }, []);

    const _onReset = useCallback(() => {
      setValue('');
    }, []);

    const _onSubmit = useCallback(() => {
      onSubmit(value);
      if (isShowBtnReset) _onReset();
      onCancel();
    }, [isShowBtnReset, _onReset, value, onSubmit, onCancel]);

    return (
      <CommentBoxWrapper>
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={e => setValue(e.target.value)}
          rows={3}
          placeholder="Write something..."
        />
        <div className="--bottom">
          <div className="act-main">
            <Button
              loading={loading}
              size="small"
              className="--btn --submit"
              onClick={_onSubmit}
            >
              <i aria-hidden className="fal fa-check" />
              <span>Submit</span>
            </Button>
            {isShowBtnReset && (
              <Button
                size="small"
                className="--btn --cancel"
                onClick={_onReset}
              >
                <i aria-hidden className="fal fa-undo-alt" />
                <span>Reset</span>
              </Button>
            )}
            {isShowBtnCancel && (
              <Button
                size="small"
                className="--btn --cancel"
                onClick={onCancel}
              >
                <i aria-hidden className="fal fa-times" />
                <span>Cancel</span>
              </Button>
            )}
          </div>
          <div className="act-extra">
            <Popover
              trigger="click"
              content={<Picker onEmojiClick={onEmojiClick} />}
              onOpenChange={isVisible => {
                if (!isVisible) inputRef.current?.focus();
              }}
              zIndex={101}
            >
              <i aria-hidden className="fas fa-grin-alt" />
            </Popover>
          </div>
        </div>
      </CommentBoxWrapper>
    );
  },
);

export default CommentBox;
